import {Injectable, OnDestroy} from '@angular/core';
import {ApiService} from './api.service';
import {Maintenance} from '../../entities/maintenance.model';
import {Subscription} from 'rxjs/Subscription';
import {timer} from 'rxjs/observable/timer';
import {LoginService} from '..';
import {Router} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {MaintenanceModalComponent} from '../maintenance-modal/maintenance-modal.component';
import {Subject} from 'rxjs/Subject';
import {deserialize} from 'json-typescript-mapper';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';

@Injectable()
export class MaintenanceService implements OnDestroy {

  fetchTimer: Subscription;
  checkTimer: Subscription;
  maintenances: Array<Maintenance>;
  maintenancesChanged: Subject<Array<Maintenance>> = new Subject<Array<Maintenance>>();
  localStorageKeys = {
    maintenances: 'maintenances',
  };
  maintenanceModalRef: any;
  announcements: BehaviorSubject<Maintenance[]>;

  constructor(private apiService: ApiService,
              private loginService: LoginService,
              private router: Router,
              private modalService: NgbModal) {
    this.announcements = new BehaviorSubject<Maintenance[]>([]);
    this.startFetchTimer(0, 10 * 60 * 1000).then(() => this.checkMaintenances());
    setInterval(() => {
      if (new Date().getSeconds() === 0) {
        this.startCheckTimer(5, 60 * 1000);
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    this.fetchTimer.unsubscribe();
    this.checkTimer.unsubscribe();
  }

  getMaintenances(): Array<Maintenance> {
    return this.maintenances;
  }

  startFetchTimer(initialDelay: number, period: number) {
    return new Promise(resolve => {
      this.fetchTimer = timer(1000, period).subscribe(() => {
        this.findMaintenances().then((maintenances: Array<Maintenance>) => {
          this.maintenances = maintenances;
          this.maintenancesChanged.next(this.maintenances);
          localStorage.setItem(this.localStorageKeys.maintenances, JSON.stringify(this.maintenances));
          this.announcements.next(this.getPlannedMaintenances());
          resolve();
        });
      });
    });
  }

  startCheckTimer(initialDelay: number, period: number) {
    this.checkTimer = timer(initialDelay, period).subscribe(d => {
      this.checkMaintenances();
    });
  }

  findMaintenances(): Promise<Array<Maintenance>> {
    return new Promise<Array<Maintenance>>(resolve => {
      this.apiService.find<Maintenance>(Maintenance, 'maintenances', null, true)
        .subscribe((maintenances: Array<Maintenance>) => {
          resolve(maintenances);
        }, error => {
          console.log('error finding maintenances', error);
          // Fallback: get local stored maintenances
          let maintenances = JSON.parse(localStorage.getItem(this.localStorageKeys.maintenances));
          if (maintenances === null) {
            maintenances = [];
          }
          resolve(maintenances);
        });
    });
  }

  checkMaintenances(): void {
    if (!this.maintenances || !this.maintenances.length || this.maintenances.length < 1) {
      return;
    } else {
      const now: Date = new Date();

      const maintenancesToday = this.maintenances.filter((maintenance: Maintenance) =>
        new Date(maintenance.startDate).getTime() <= now.getTime()
        && (new Date(maintenance.endDate).getTime() > now.getTime() || maintenance.endDate === null));

      if (maintenancesToday.length > 0) {

        const maintenancesNow = maintenancesToday.filter((maintenance: Maintenance) => {
          const startTime: Date = new Date();
          startTime.setHours(
            parseInt(maintenance.startTime.split(':')[0], 10),
            parseInt(maintenance.startTime.split(':')[1], 10),
            0
          );

          const endTime: Date = new Date();
          endTime.setHours(
            parseInt(maintenance.endTime.split(':')[0], 10),
            parseInt(maintenance.endTime.split(':')[1], 10),
            0
          );

          if (maintenance.startTime.split(':')[0] > maintenance.endTime.split(':')[0]) {
            endTime.setDate(endTime.getDate() + 1);
          }

          return startTime.getTime() <= now.getTime() && endTime.getTime() > now.getTime();
        });

        if (maintenancesNow.length > 0) {
          // maintenance mode now: logout and inform user
          this.loginService.logout();
          this.router.navigate(['/login']).then();
          // TODO decide which maintenance is shown in modal. for the moment: take first
          this.showMaintenanceModal(maintenancesNow[0]);
        } else if (this.maintenanceModalRef) {
          // close and reset modalRef
          this.maintenanceModalRef.close();
          this.maintenanceModalRef = null;
        }
      }
    }
  }

  showMaintenanceModal(maintenance: Maintenance) {
    if (!this.maintenanceModalRef) {
      this.maintenanceModalRef = this.modalService.open(MaintenanceModalComponent, {backdrop: 'static'});
    }
    this.maintenanceModalRef.componentInstance.maintenance = maintenance;
  }

  saveMaintenance(maintenance: Maintenance): Promise<Maintenance> {
    return new Promise<Maintenance>(resolve => {
      if (maintenance.id !== null) {
        this.apiService.put(maintenance, 'maintenance', true).subscribe(postResult => {
          resolve(deserialize<Maintenance>(Maintenance, postResult));
          this.findMaintenances().then((maintenances: Array<Maintenance>) => {
            this.maintenances = maintenances;
            this.maintenancesChanged.next(this.maintenances);
            localStorage.setItem(this.localStorageKeys.maintenances, JSON.stringify(this.maintenances));
          });
        });
      } else {
        this.apiService.post(maintenance, 'maintenance', true).subscribe(postResult => {
          resolve(deserialize<Maintenance>(Maintenance, postResult));
          this.findMaintenances().then((maintenances: Array<Maintenance>) => {
            this.maintenances = maintenances;
            this.maintenancesChanged.next(this.maintenances);
            localStorage.setItem(this.localStorageKeys.maintenances, JSON.stringify(this.maintenances));
          });
        });
      }
    });
  }

  removeMaintenance(maintenance: Maintenance): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (maintenance.id) {
        this.apiService.delete(['maintenance', maintenance.id].join('/'), true).subscribe((deleteResult) => {
          resolve();
          this.findMaintenances().then((maintenances: Array<Maintenance>) => {
            this.maintenances = maintenances;
            this.maintenancesChanged.next(this.maintenances);
            localStorage.setItem(this.localStorageKeys.maintenances, JSON.stringify(this.maintenances));
          });
        });
      } else {
        reject('maintenance without id!');
      }
    });
  }

  /**
   * Returns an array of future maintenances that are allowed to be announced
   */
  getPlannedMaintenances(): Maintenance[] {
    if (this.maintenances.length === 0) { return []; }

    return this.maintenances.filter((m) => {
      if (!m.announcementStart) { return false; }
      // Build proper date objects, so that we can easily compare them to the current date
      const startDate = new Date(m.startDate);
      const hour = parseInt(m.startTime.split(':')[0], 10);
      const min = parseInt(m.startTime.split(':')[1], 10);
      startDate.setHours(hour, min);
      const announce = new Date(m.announcementStart);
      const now = new Date(Date.now());

      return startDate > now && announce <= now;
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/services/maintenance.service.ts